import React, { useState, useRef } from 'react';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';

const FEATURE_KEY_SUBMISSION_GROUPS = 'submission_groups_v1';

const FeatureAnnouncementModal = (props) => {
  const {
    show = false,
    showSubmissionGroupsAnnouncement = false,
    featureKey,
    dismissFeatureUrl,
    submissionGroupsUrl,
    planUrl,
    hasDuesPro = true,
    featureAnnouncementVideoUrl,
    featureAnnouncementImageDashboardUrl,
    featureAnnouncementImageWorkflowUrl,
    featureAnnouncementKey = FEATURE_KEY_SUBMISSION_GROUPS,
  } = props;

  const shouldShow = show || showSubmissionGroupsAnnouncement;
  const [open, setOpen] = useState(shouldShow);
  const [dismissing, setDismissing] = useState(false);
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);

  const handlePlay = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setPlaying(true);
    }
  };

  const handleDismiss = async () => {
    if (videoRef.current) videoRef.current.pause();
    const key = featureKey || featureAnnouncementKey;
    if (!dismissFeatureUrl || !key) {
      setOpen(false);
      return;
    }
    setDismissing(true);
    try {
      const csrfToken = document.querySelector("[name='csrf-token']")?.content;
      await fetch(dismissFeatureUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          Accept: 'application/json',
        },
        body: JSON.stringify({ feature_key: key }),
      });
    } catch (e) {
      // still close on error
    }
    setDismissing(false);
    setOpen(false);
  };

  const primaryUrl = hasDuesPro ? submissionGroupsUrl : planUrl;
  const primaryLabel = hasDuesPro ? 'Get Started' : 'Upgrade to Pro';

  const handlePrimaryClick = async (e) => {
    e.preventDefault();
    if (videoRef.current) videoRef.current.pause();
    const key = featureKey || featureAnnouncementKey;
    if (dismissFeatureUrl && key) {
      try {
        const csrfToken = document.querySelector("[name='csrf-token']")?.content;
        await fetch(dismissFeatureUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
            Accept: 'application/json',
          },
          body: JSON.stringify({ feature_key: key }),
        });
      } catch (e) {
        // navigate anyway
      }
    }
    window.location.href = primaryUrl;
  };

  const isSubmissionGroups = (featureKey || featureAnnouncementKey) === FEATURE_KEY_SUBMISSION_GROUPS;
  if (!open || !isSubmissionGroups) return null;

  const features = [
    { label: 'Custom form builder', color: 'teal' },
    { label: 'Membership applications', color: 'indigo' },
    { label: 'Review & approve dashboard', color: 'amber' },
    { label: 'Multi-fund support', color: 'purple' },
  ];

  const colorMap = {
    teal: 'bg-teal-500',
    indigo: 'bg-indigo-500',
    amber: 'bg-amber-500',
    purple: 'bg-purple-500',
  };

  return (
    <Transition show={open} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleDismiss}>
        <TransitionChild
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="relative w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all">
                {/* Close button */}
                <div className="absolute right-3 top-3 z-10">
                  <button
                    type="button"
                    onClick={handleDismiss}
                    disabled={dismissing}
                    className="rounded-full p-1.5 bg-white/80 backdrop-blur text-gray-500 hover:bg-white hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:opacity-50 shadow-sm"
                    aria-label="Close"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Video hero */}
                {featureAnnouncementVideoUrl && (
                  <div className="relative bg-gray-900 cursor-pointer" onClick={!playing ? handlePlay : undefined}>
                    <video
                      ref={videoRef}
                      src={featureAnnouncementVideoUrl}
                      controls={playing}
                      playsInline
                      className="w-full"
                      style={{ maxHeight: '360px', objectFit: 'contain' }}
                      onPlay={() => setPlaying(true)}
                      onPause={() => setPlaying(false)}
                      onEnded={() => setPlaying(false)}
                    />
                    {!playing && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <button
                          type="button"
                          onClick={handlePlay}
                          className="flex h-16 w-16 items-center justify-center rounded-full bg-white/90 shadow-lg backdrop-blur transition hover:bg-white hover:scale-110"
                        >
                          <svg className="h-8 w-8 text-gray-900 ml-1" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Fallback: show images if no video */}
                {!featureAnnouncementVideoUrl && (featureAnnouncementImageDashboardUrl || featureAnnouncementImageWorkflowUrl) && (
                  <div className="grid grid-cols-2 gap-1 bg-gray-100 p-1">
                    {featureAnnouncementImageDashboardUrl && (
                      <img src={featureAnnouncementImageDashboardUrl} alt="Dashboard" className="w-full rounded object-cover" />
                    )}
                    {featureAnnouncementImageWorkflowUrl && (
                      <img src={featureAnnouncementImageWorkflowUrl} alt="Workflow" className="w-full rounded object-cover" />
                    )}
                  </div>
                )}

                {/* Content */}
                <div className="p-6">
                  <div className="text-center">
                    <DialogTitle as="h2" className="text-xl font-bold tracking-tight text-gray-900">
                      Submission Groups & Membership Applications
                    </DialogTitle>
                    <p className="mt-1.5 text-sm text-gray-500">
                      Collect custom form submissions and manage membership applications from your dashboard.
                    </p>
                  </div>

                  {/* Compact feature pills */}
                  <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                    {features.map((f) => (
                      <span
                        key={f.label}
                        className="inline-flex items-center gap-1.5 rounded-full bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-200"
                      >
                        <span className={`h-1.5 w-1.5 rounded-full ${colorMap[f.color]}`} />
                        {f.label}
                      </span>
                    ))}
                  </div>

                  {/* Actions */}
                  <div className="mt-6 flex items-center justify-center gap-3">
                    <a
                      href={primaryUrl}
                      onClick={handlePrimaryClick}
                      className="inline-flex items-center justify-center rounded-lg bg-teal-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
                    >
                      {primaryLabel}
                    </a>
                    <button
                      type="button"
                      onClick={handleDismiss}
                      disabled={dismissing}
                      className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-50"
                    >
                      {dismissing ? 'Saving…' : 'Got it'}
                    </button>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default FeatureAnnouncementModal;
